import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { apiErrorMessage } from '@shared/api/client';
import { uploadKycDoc } from '@shared/api/associates';
import { useKycStatus, useSubmitKyc } from '@shared/hooks/use-associate';
import { cn } from '@/lib/cn';

const invalidInputCls = 'border-red-400 focus:border-red-500 focus:ring-red-500/30';

export function KycPage() {
  const { data: status } = useKycStatus();
  const submit = useSubmitKyc();

  const [aadhaar, setAadhaar] = useState('');
  const [pan, setPan] = useState('');
  const [gstin, setGstin] = useState('');
  const [address, setAddress] = useState('');
  const [bankAccount, setBankAccount] = useState('');
  const [bankName, setBankName] = useState('');
  const [bankBranch, setBankBranch] = useState('');
  const [ifsc, setIfsc] = useState('');

  const [aadhaarKey, setAadhaarKey] = useState<string | null>(null);
  const [panKey, setPanKey] = useState<string | null>(null);
  const aadhaarInputRef = useRef<HTMLInputElement>(null);
  const panInputRef = useRef<HTMLInputElement>(null);

  const aadhaarUpload = useMutation({
    mutationFn: (f: File) => uploadKycDoc('AADHAAR', f),
    onSuccess: (key) => setAadhaarKey(key),
  });
  const panUpload = useMutation({
    mutationFn: (f: File) => uploadKycDoc('PAN', f),
    onSuccess: (key) => setPanKey(key),
  });

  // Per-field validation. Each entry is `undefined` when valid, or a
  // user-visible message when not. `errors` is recomputed on every render
  // so messages disappear the instant the user fixes them.
  const errors = {
    aadhaar: /^[0-9]{12}$/.test(aadhaar) ? undefined : 'Enter the 12-digit Aadhaar number',
    aadhaarDoc: aadhaarKey ? undefined : 'Upload a scan of your Aadhaar',
    pan: /^[A-Z]{5}[0-9]{4}[A-Z]$/.test(pan.toUpperCase())
      ? undefined
      : 'PAN must look like AAAAA9999A',
    address: address.trim().length > 0 ? undefined : 'Address is required',
    bankAccount: /^[0-9]{9,18}$/.test(bankAccount)
      ? undefined
      : 'Account number must be 9–18 digits',
    ifsc: /^[A-Z]{4}0[A-Z0-9]{6}$/.test(ifsc.toUpperCase())
      ? undefined
      : 'IFSC must look like ABCD0123456',
    bankName: bankName.trim().length > 0 ? undefined : 'Bank name is required',
    bankBranch: bankBranch.trim().length > 0 ? undefined : 'Branch is required',
  } as const;
  const hasErrors = Object.values(errors).some(Boolean);

  // Errors only render after the first submit attempt — keeps the form
  // quiet while the user is initially typing, but flags everything the
  // moment they click Submit. Stays sticky after that: errors update live
  // as the user fixes them.
  const [showErrors, setShowErrors] = useState(false);
  const err = (k: keyof typeof errors) => (showErrors ? errors[k] : undefined);

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (hasErrors) {
      setShowErrors(true);
      return;
    }
    submit.mutate({
      aadhaarNumber: aadhaar,
      aadhaarDocS3Key: aadhaarKey as string,
      panNumber: pan.toUpperCase(),
      panDocS3Key: panKey ?? undefined,
      gstin: gstin.trim() || undefined,
      address: address.trim(),
      bankAccountNumber: bankAccount,
      bankName: bankName.trim(),
      bankBranch: bankBranch.trim(),
      ifscCode: ifsc.toUpperCase(),
    });
  }

  const alreadySubmitted = status && status.status !== 'NOT_SUBMITTED' && submit.isIdle;

  if (submit.isSuccess || status?.status === 'VERIFIED') {
    return (
      <div className="mx-auto max-w-xl px-4 py-10 sm:px-6 sm:py-16 text-center">
        <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-brand-50">
          <svg
            width="36"
            height="36"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#15803d"
            strokeWidth="2"
            aria-hidden
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </div>
        <h1 className="mt-4 text-xl font-semibold text-gray-900">
          {status?.status === 'VERIFIED' ? 'KYC verified' : 'KYC details submitted successfully'}
        </h1>
        <p className="mt-2 text-sm text-gray-600">
          {status?.status === 'VERIFIED'
            ? 'You can start accepting assignments.'
            : 'An admin will review your submission. You\u2019ll be notified once approved.'}
        </p>
        <div className="mt-6 flex justify-center gap-3">
          <Link to="/associate/services">
            <Button variant="outline">Pick services you offer</Button>
          </Link>
          <Link to="/associate/transactions">
            <Button>Go to transactions</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full px-4 py-6 sm:px-6 sm:py-8">
      <h1 className="text-xl font-semibold text-gray-900">KYC Verification</h1>
      <p className="text-sm text-gray-600">
        Submit your Aadhaar, PAN, and bank details. All numbers are encrypted at rest.
      </p>

      {alreadySubmitted ? (
        <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          Current status: <span className="font-semibold">{status?.status}</span>.
          {status?.rejectionReason ? (
            <>
              {' '}
              Reason: <em>{status.rejectionReason}</em>. You can resubmit below.
            </>
          ) : null}
        </div>
      ) : null}

      <form onSubmit={onSubmit} noValidate className="mt-6 space-y-8">
        <Section title="Identity">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Aadhaar number *" error={err('aadhaar')}>
              <Input
                value={aadhaar}
                onChange={(e) => setAadhaar(e.target.value.replace(/\D/g, '').slice(0, 12))}
                placeholder="12-digit Aadhaar"
                inputMode="numeric"
                maxLength={12}
                aria-invalid={!!err('aadhaar')}
                className={err('aadhaar') ? invalidInputCls : undefined}
              />
            </Field>
            <DocField
              label="Aadhaar scan (JPG/PNG/PDF, ≤10 MB) *"
              inputRef={aadhaarInputRef}
              isUploaded={!!aadhaarKey}
              uploading={aadhaarUpload.isPending}
              onFile={(f) => aadhaarUpload.mutate(f)}
              uploadError={aadhaarUpload.error as Error | null}
              validationError={err('aadhaarDoc')}
            />
            <Field label="PAN *" error={err('pan')}>
              <Input
                value={pan}
                onChange={(e) => setPan(e.target.value.toUpperCase().slice(0, 10))}
                placeholder="AAAAA9999A"
                maxLength={10}
                aria-invalid={!!err('pan')}
                className={err('pan') ? invalidInputCls : undefined}
              />
            </Field>
            <DocField
              label="PAN scan (optional)"
              inputRef={panInputRef}
              isUploaded={!!panKey}
              uploading={panUpload.isPending}
              onFile={(f) => panUpload.mutate(f)}
              uploadError={panUpload.error as Error | null}
            />
            <Field label="GSTIN (optional)">
              <Input
                value={gstin}
                onChange={(e) => setGstin(e.target.value.toUpperCase())}
                placeholder="29AAAAA0000A1Z5"
                maxLength={15}
              />
            </Field>
            <Field label="Address *" className="sm:col-span-2" error={err('address')}>
              <textarea
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                rows={2}
                aria-invalid={!!err('address')}
                className={cn(
                  'w-full rounded-md border bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2',
                  err('address')
                    ? 'border-red-400 focus:border-red-500 focus:ring-red-500/30'
                    : 'border-gray-300 focus:border-brand-600 focus:ring-brand-500/30',
                )}
                placeholder="Full address as on records"
              />
            </Field>
          </div>
        </Section>

        <Section title="Bank details">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Account number *" error={err('bankAccount')}>
              <Input
                value={bankAccount}
                onChange={(e) => setBankAccount(e.target.value.replace(/\D/g, '').slice(0, 18))}
                placeholder="9–18 digits"
                inputMode="numeric"
                aria-invalid={!!err('bankAccount')}
                className={err('bankAccount') ? invalidInputCls : undefined}
              />
            </Field>
            <Field label="IFSC code *" error={err('ifsc')}>
              <Input
                value={ifsc}
                onChange={(e) => setIfsc(e.target.value.toUpperCase().slice(0, 11))}
                placeholder="SBIN0001234"
                maxLength={11}
                aria-invalid={!!err('ifsc')}
                className={err('ifsc') ? invalidInputCls : undefined}
              />
            </Field>
            <Field label="Bank name *" error={err('bankName')}>
              <Input
                value={bankName}
                onChange={(e) => setBankName(e.target.value)}
                aria-invalid={!!err('bankName')}
                className={err('bankName') ? invalidInputCls : undefined}
              />
            </Field>
            <Field label="Branch *" error={err('bankBranch')}>
              <Input
                value={bankBranch}
                onChange={(e) => setBankBranch(e.target.value)}
                aria-invalid={!!err('bankBranch')}
                className={err('bankBranch') ? invalidInputCls : undefined}
              />
            </Field>
          </div>
        </Section>

        {showErrors && hasErrors ? (
          <p className="text-sm text-red-600">
            Please fix the highlighted fields above to continue.
          </p>
        ) : null}

        {submit.isError ? (
          <p className="text-sm text-red-600">{apiErrorMessage(submit.error)}</p>
        ) : null}

        <div className="flex justify-end">
          <Button type="submit" disabled={submit.isPending}>
            {submit.isPending ? 'Submitting…' : 'Submit KYC'}
          </Button>
        </div>
      </form>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-xl border border-gray-200 bg-white p-4 sm:p-6">
      <h2 className="mb-4 text-sm font-semibold text-gray-900">{title}</h2>
      {children}
    </section>
  );
}

function Field({
  label,
  className,
  error,
  children,
}: {
  label: string;
  className?: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <label className={cn('block', className)}>
      <div className="mb-1 text-xs font-medium text-gray-700">{label}</div>
      {children}
      {error ? <p className="mt-1 text-xs text-red-600">{error}</p> : null}
    </label>
  );
}

function DocField({
  label,
  inputRef,
  isUploaded,
  uploading,
  onFile,
  uploadError,
  validationError,
}: {
  label: string;
  inputRef: React.RefObject<HTMLInputElement>;
  isUploaded: boolean;
  uploading: boolean;
  onFile: (f: File) => void;
  uploadError: Error | null;
  validationError?: string;
}) {
  const invalid = !!validationError;
  return (
    <div>
      <div className="mb-1 text-xs font-medium text-gray-700">{label}</div>
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        aria-invalid={invalid}
        className={cn(
          'flex w-full items-center justify-between rounded-md border border-dashed px-3 py-2 text-sm',
          isUploaded
            ? 'border-brand-300 bg-brand-50/50 text-brand-800'
            : invalid
              ? 'border-red-400 bg-white text-gray-600 hover:bg-gray-50'
              : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50',
        )}
      >
        <span>
          {uploading ? 'Uploading…' : isUploaded ? 'Uploaded ✓ — replace' : 'Click to upload'}
        </span>
        <span className="text-xs">{isUploaded ? 'Change' : 'Browse'}</span>
      </button>
      <input
        ref={inputRef}
        type="file"
        hidden
        accept="image/jpeg,image/png,application/pdf"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onFile(f);
          e.target.value = '';
        }}
      />
      {uploadError ? <p className="mt-1 text-xs text-red-600">{uploadError.message}</p> : null}
      {!uploadError && validationError ? (
        <p className="mt-1 text-xs text-red-600">{validationError}</p>
      ) : null}
    </div>
  );
}
